<template>
  <div :class="clContainer()">
    <span :class="clSub('label', 'label')">{{ label }}</span>
    <div :class="clSub('text', textClass)">
      <slot :files="files" :text="text" :removeFile="removeFile" :state="state">
        <ul v-if="multiple && files.length">
          <li v-for="(file, index) in files" :key="index">
            <button v-if="state == 'edit'" @click="removeFile(index)">
              <img src="../../../../img/icons/cross.svg" />
            </button>
            <a
              v-if="hasRoute(file)"
              :href="baseUrl + routePrefix + file.route"
              target="_blank"
              @click="
                $emit('file-clicked', {
                  event: $event,
                  file: file,
                  label: label,
                })
              "
              >{{ file.name }}</a
            >
            <span v-else>{{ file.name }}</span>
          </li>
        </ul>

        <div v-if="!multiple && files.length">
          <button v-if="state == 'edit'" @click="removeFile(0)">
            <img src="../../../../img/icons/cross.svg" />
          </button>
          <a
            v-if="hasRoute(files[0])"
            :href="baseUrl + routePrefix + files[0].route"
            target="_blank"
            @click="
              $emit('file-clicked', {
                event: $event,
                file: files[0],
                label: label,
              })
            "
            >{{ files[0].name }}</a
          >
          <span v-else>{{ files[0].name }}</span>
        </div>

        <span v-if="!files.length">{{ text }}</span>
      </slot>
    </div>

    <div :class="clSub('input')">
      <input
        type="file"
        :id="fileInputId"
        :name="name"
        :multiple="multiple"
        ref="fileInput"
        @change="onFilesChanged"
        :accept="accept"
      />
      <label
        :for="fileInputId"
        :class="labelFileSelectClass"
        :tabindex="tabindex"
      >
        {{ labelFileSelect }}
      </label>
    </div>

    <div :class="clSub('errors')">
      <div
        v-for="(error, index) in errors"
        :class="clSub('error')"
        :key="index"
      >
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import Base from "./Base";
import Uploader from "../../../modules/uploader";
import { getElementData } from "../../../helpers/dom";

export default {
  extends: Base,
  inject: {
    baseUrl: { default: "" },
  },
  props: {
    typeClass: {
      type: String,
      default: "file",
    },
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    uploadUrl: {
      type: String,
    },
    routePrefix: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    labelFileSelect: {
      type: String,
      default: "Select File",
    },
    labelFileSelectClass: {
      type: String,
    },
    initalFilesSelector: {
      type: String,
    },
    allowedMimeTypes: {
      type: Array,
    },
  },
  data() {
    return {
      fileInputId: "editable_file_" + (this.id ? this.id : this._uid) + "_file",
    };
  },
  computed: {
    hasValue() {
      return this.files.length > 0;
    },
    files() {
      return this.data;
    },
    textClass() {
      if (!this.files.length) {
        return this.$parent.noValueClass ? this.$parent.noValueClass : "";
      }

      return "";
    },
    accept() {
      if (!this.allowedMimeTypes) {
        return undefined;
      }
      const accept = [];

      for (const mimeType of this.allowedMimeTypes) {
        let mimeTypeParts = mimeType.split("/");
        let acceptMimeType = mimeTypeParts[0] + "/*";

        if (!accept.includes(acceptMimeType)) {
          accept.push(acceptMimeType);
        }
      }

      return accept.join(",");
    },
  },
  created() {
    this.addDataProcessor(this.setFilesData, "set", 0);
    this.addDataProcessor((newData) => {
      if (
        this.files[0] &&
        Object.keys(this.files[0]).length === 0 &&
        this.files[0].constructor === Object
      ) {
        this.removeFile(0);
      }
      this.dataChanged();
    }, "setAfter");
  },
  mounted() {
    if (this.uploadUrl) {
      let uploader = new Uploader(this.$refs.fileInput, null, this.uploadUrl);

      uploader.onUploaded = (fileData) => {
        if (this.multiple) {
          this.files.push(fileData);
        } else {
          this.setFiles([fileData]);
        }
      };
    }

    let files = [];

    if (this.initalFilesSelector) {
      let fileElements = this.$el.querySelectorAll(this.initalFilesSelector);

      fileElements.forEach((fileElem) => {
        files.push(getElementData(fileElem));
        fileElem.remove();
      });
    }

    if (files.length) {
      this.setFiles(files);
      this.dataChanged();
    }
  },
  methods: {
    onFilesChanged(e) {
      const newFiles = this.multiple
        ? this.files
          ? this.files.concat(Array.from(e.target.files))
          : Array.from(e.target.files)
        : Array.from(e.target.files);
      if (this.allowedMimeTypes) {
        for (const file of newFiles) {
          if (file.type && !this.allowedMimeTypes.includes(file.type)) {
            this.setErrors(["Ungültiger Dateityp"]);
            return false;
          }
        }
      }

      this.setErrors(null);
      this.setFiles(newFiles);
      this.dataChanged();
    },
    setFiles(files) {
      this.files.splice(0, this.files.length, ...files);
    },
    removeFile(file) {
      let removeIndex =
        typeof file != "number" ? this.files.indexOf(file) : file;

      if (removeIndex != -1) {
        this.files.splice(removeIndex, 1);
      }

      if (this.files.length == 0) {
        this.setErrors(null);
      }

      this.dataChanged();
    },
    handleSaveResponse(response) {
      if ("file" in response && "files" in response) {
        this.files = this.multiple ? response.files : response.file;
      }
    },
    setFilesData(newData) {
      if (!Array.isArray(newData)) {
        newData = newData ? [newData] : [];
      }

      return newData;
    },
    hasRoute(file) {
      return !(file instanceof File) && file.route;
    },
  },
};
</script>

<style lang="scss" scoped>
input[type="file"] {
  display: none;
}
</style>